import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AlertCircle, ChevronLeft, ChevronRight, History, Search, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { EmptyState } from "@/components/empty-state";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth-context";
import { getModerationQueue, recordModerationDecision } from "@/lib/moderation.functions";
import { fetchOpenReports } from "@/lib/queries";
import { TagChip } from "@/components/tag-chip";

export const Route = createFileRoute("/_authenticated/moderation")({
  head: () => ({
    meta: [
      { title: "Moderation queue — Quibblies" },
      {
        name: "description",
        content: "Staff review queue for submitted publications and open community reports.",
      },
      { property: "og:title", content: "Moderation queue — Quibblies" },
      { property: "og:description", content: "Review submitted publications on Quibblies." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: ModerationPage,
});

const CONCERNS = [
  { value: "privacy", label: "Privacy concern" },
  { value: "personal_information", label: "Personal information concern" },
  { value: "consent", label: "Consent concern" },
  { value: "needs_senior_review", label: "Needs senior review" },
  { value: "tag_spam", label: "Tag spam" },
  { value: "misleading_tags", label: "Misleading tags" },
] as const;

/** Tags are part of the review: misleading hashtags mis-route readers. */
function SubmissionTags({ tags }: { tags: Array<{ id: string; slug: string; name: string }> }) {
  if (tags.length === 0) return null;
  return (
    <div className="mt-3">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
        Tags ({tags.length}/15)
      </p>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {tags.map((tag) => (
          <TagChip key={tag.id} tag={tag} />
        ))}
      </div>
    </div>
  );
}

function ModerationPage() {
  const { isStaff, loading } = useAuth();
  const queryClient = useQueryClient();
  const loadQueue = useServerFn(getModerationQueue);
  const saveDecision = useServerFn(recordModerationDecision);
  const [feedback, setFeedback] = React.useState<Record<string, string>>({});
  const [concerns, setConcerns] = React.useState<Record<string, string[]>>({});
  const [search, setSearch] = React.useState("");
  const [tag, setTag] = React.useState("all");
  const [date, setDate] = React.useState("all");
  const [page, setPage] = React.useState(1);
  const pageSize = 10;

  function toggleConcern(submissionId: string, concern: string) {
    setConcerns((prev) => {
      const current = prev[submissionId] ?? [];
      return {
        ...prev,
        [submissionId]: current.includes(concern)
          ? current.filter((item) => item !== concern)
          : [...current, concern],
      };
    });
  }

  const queue = useQuery({
    queryKey: ["moderation-queue"],
    queryFn: () => loadQueue(),
    enabled: isStaff,
  });
  const reports = useQuery({
    queryKey: ["open-reports"],
    queryFn: fetchOpenReports,
    enabled: isStaff,
  });

  const decide = useMutation({
    mutationFn: async ({
      submissionId,
      storyId,
      decision,
    }: {
      submissionId: string;
      storyId: string;
      decision: "approved" | "changes_requested" | "rejected" | "escalated";
    }) => {
      await saveDecision({
        data: {
          submissionId,
          storyId,
        decision,
          note: feedback[submissionId] ?? "",
          concerns: concerns[submissionId] ?? [],
        },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["moderation-queue"] });
      void queryClient.invalidateQueries({ queryKey: ["stories"] });
      toast.success("Decision recorded");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const allTags = React.useMemo(() => {
    const values = new Map<string, string>();
    for (const submission of queue.data ?? []) {
      for (const item of submission.tags ?? []) values.set(item.slug, item.name);
    }
    return Array.from(values, ([slug, name]) => ({ slug, name })).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [queue.data]);

  const filtered = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    const now = Date.now();
    const cutoff =
      date === "7" ? now - 7 * 86400000 : date === "30" ? now - 30 * 86400000 : null;
    return (queue.data ?? []).filter((submission) => {
      const searchable = [
        submission.stories?.title,
        submission.profiles?.username,
        submission.profiles?.display_name,
        ...(submission.tags ?? []).flatMap((item) => [item.name, item.slug]),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return (
        (!term || searchable.includes(term)) &&
        (tag === "all" || (submission.tags ?? []).some((item) => item.slug === tag)) &&
        (cutoff === null || new Date(submission.submitted_at).getTime() >= cutoff)
      );
    });
  }, [date, queue.data, search, tag]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const visible = filtered.slice((page - 1) * pageSize, page * pageSize);

  React.useEffect(() => setPage(1), [search, tag, date]);
  React.useEffect(() => setPage((current) => Math.min(current, pageCount)), [pageCount]);

  if (!loading && !isStaff) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6">
        <EmptyState
          icon={ShieldCheck}
          title="Moderators only"
          description="This area is limited to moderators and administrators."
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
      <h1 className="font-display text-4xl text-foreground">Moderation</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Nothing publishes without a decision here.
      </p>

      <section className="mt-10">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h2 className="font-display text-2xl text-foreground">Review queue</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {queue.isSuccess ? `${queue.data.length} waiting for review` : "Checking submissions…"}
            </p>
          </div>
          {queue.isFetching && !queue.isLoading ? (
            <span className="text-xs text-muted-foreground">Refreshing…</span>
          ) : null}
        </div>

        {queue.isError ? (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle />
            <AlertTitle>Queue failed to load</AlertTitle>
            <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
              <span>{queue.error instanceof Error ? queue.error.message : "Please try again."}</span>
              <Button variant="outline" size="sm" onClick={() => void queue.refetch()}>
                Try again
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}

        {queue.isSuccess ? (
          <div className="mt-4 grid gap-3 border-y border-border/70 py-4 md:grid-cols-[minmax(0,1fr)_180px_180px]">
            <label className="relative">
              <span className="sr-only">Search submissions</span>
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search title, author, or tag"
              />
            </label>
            <Select value={tag} onValueChange={setTag}>
              <SelectTrigger aria-label="Filter by tag"><SelectValue placeholder="All tags" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tags</SelectItem>
                {allTags.map((item) => <SelectItem key={item.slug} value={item.slug}>#{item.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={date} onValueChange={setDate}>
              <SelectTrigger aria-label="Filter by submission date"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any submission date</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ) : null}

        <div className="mt-4 space-y-4">
          {queue.isLoading ? (
            Array.from({ length: 2 }).map((_, index) => (
              <Skeleton key={index} className="h-44 rounded-2xl" />
            ))
          ) : queue.isError ? null : visible.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              title={(queue.data ?? []).length === 0 ? "Queue is clear" : "No matching submissions"}
              description={(queue.data ?? []).length === 0 ? "No submissions are waiting for review right now." : "Try changing your search or filters."}
            />
          ) : (
            visible.map((submission) => (
              <article
                key={submission.id}
                className="rounded-2xl border border-border/70 bg-gradient-card p-6"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="font-display text-xl text-foreground">
                    {submission.stories?.title ?? "Untitled"}
                  </h3>
                  <span className="text-xs text-muted-foreground">
                    by @{submission.profiles?.username} · {submission.stories?.word_count ?? 0} words
                  </span>
                </div>
                {submission.stories?.content_types ? (
                  <span className="mt-2 inline-block rounded-full bg-primary/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground">
                    {submission.stories.content_types.name}
                  </span>
                ) : null}
                {submission.stories?.content_types?.is_personal ? (
                  <p className="mt-2 text-xs text-primary-glow">
                    Personal writing — check for identifying details about real people before
                    approving.
                  </p>
                ) : null}
                {submission.stories?.description ? (
                  <p className="mt-2 text-sm text-muted-foreground">
                    {submission.stories.description}
                  </p>
                ) : null}

                <SubmissionTags tags={submission.tags ?? []} />

                <details className="mt-4 border-y border-border/60 py-3">
                  <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-medium text-foreground">
                    <History className="h-4 w-4" />
                    Audit trail ({submission.history.length})
                  </summary>
                  <ol className="mt-3 space-y-3 border-l border-border pl-4">
                    {submission.history.map((event) => (
                      <li key={event.id} className="text-sm">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <span className="font-medium capitalize text-foreground">{event.action.replaceAll("_", " ")}</span>
                          <time className="text-xs text-muted-foreground">{new Date(event.createdAt).toLocaleString("en-GB")}</time>
                        </div>
                        {event.actor ? <p className="text-xs text-muted-foreground">Reviewed by {event.actor.display_name} (@{event.actor.username})</p> : null}
                        {event.note ? <p className="mt-1 text-muted-foreground">{event.note}</p> : null}
                        {event.concerns.length > 0 ? <p className="mt-1 text-xs text-muted-foreground">Concerns: {event.concerns.join(", ")}</p> : null}
                      </li>
                    ))}
                  </ol>
                </details>

                <div className="mt-4 flex flex-wrap gap-2">
                  {CONCERNS.map((concern) => {
                    const active = (concerns[submission.id] ?? []).includes(concern.value);
                    return (
                      <button
                        key={concern.value}
                        type="button"
                        onClick={() => toggleConcern(submission.id, concern.value)}
                        className={
                          "rounded-full border px-3 py-1 text-xs transition-smooth " +
                          (active
                            ? "border-primary/60 bg-accent text-foreground"
                            : "border-border/70 text-muted-foreground hover:text-foreground")
                        }
                      >
                        {concern.label}
                      </button>
                    );
                  })}
                </div>

                <Textarea
                  className="mt-4"
                  rows={3}
                  maxLength={1000}
                  placeholder="Feedback for the author (required for changes or rejection)"
                  value={feedback[submission.id] ?? ""}
                  onChange={(e) =>
                    setFeedback((prev) => ({ ...prev, [submission.id]: e.target.value }))
                  }
                />

                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    variant="hero"
                    size="sm"
                    disabled={decide.isPending}
                    onClick={() =>
                      decide.mutate({
                        submissionId: submission.id,
                        storyId: submission.story_id,
                        decision: "approved",
                      })
                    }
                  >
                    Approve & publish
                  </Button>
                  <Button
                    variant="soft"
                    size="sm"
                    disabled={decide.isPending}
                    onClick={() =>
                      decide.mutate({
                        submissionId: submission.id,
                        storyId: submission.story_id,
                        decision: "changes_requested",
                      })
                    }
                  >
                    Request changes
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={decide.isPending}
                    onClick={() =>
                      decide.mutate({
                        submissionId: submission.id,
                        storyId: submission.story_id,
                        decision: "rejected",
                      })
                    }
                  >
                    Reject
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={decide.isPending}
                    onClick={() =>
                      decide.mutate({
                        submissionId: submission.id,
                        storyId: submission.story_id,
                        decision: "escalated",
                      })
                    }
                  >
                    Escalate to admin
                  </Button>
                </div>
              </article>
            ))
          )}
        </div>
        {filtered.length > pageSize ? (
          <div className="mt-5 flex items-center justify-between gap-4 border-t border-border/70 pt-4">
            <p className="text-sm text-muted-foreground">Page {page} of {pageCount} · {filtered.length} results</p>
            <div className="flex gap-2">
              <Button variant="outline" size="icon" aria-label="Previous page" disabled={page === 1} onClick={() => setPage((value) => Math.max(1, value - 1))}><ChevronLeft /></Button>
              <Button variant="outline" size="icon" aria-label="Next page" disabled={page === pageCount} onClick={() => setPage((value) => Math.min(pageCount, value + 1))}><ChevronRight /></Button>
            </div>
          </div>
        ) : null}
      </section>

      <section className="mt-12">
        <h2 className="font-display text-2xl text-foreground">Open reports</h2>
        <div className="mt-4 space-y-3">
          {(reports.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No open reports.</p>
          ) : (
            reports.data!.map((report) => (
              <article
                key={report.id}
                className="rounded-2xl border border-border/70 bg-surface/50 p-4"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs uppercase tracking-wide text-primary-glow">
                    {report.target_type}
                  </span>
                  <span className="text-xs text-muted-foreground">{report.status}</span>
                </div>
                <p className="mt-2 text-sm text-foreground">{report.reason}</p>
                {report.details ? (
                  <p className="mt-1 text-sm text-muted-foreground">{report.details}</p>
                ) : null}
              </article>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
